import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConfigSectionState,
  DiagnosticsState,
  RunDiagnosticRequest,
  VoipState,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, Input, Pill, Spinner } from '../../components/ui';
import { useUnsavedDraft } from '../../components/ui/CollapsibleGroup';
import { SecurityGroupControls } from './SecurityControls';

type Notify = (message: string, tone?: 'info' | 'danger') => void;

const enc = encodeURIComponent;

/**
 * A model-aware Configs section (Management, Network, Time, WiFi advanced),
 * rendered from `GET /devices/:id/config/:section`. The engine returns only the
 * controls the CPE actually reports, so this shows whatever the model carries and
 * an honest empty note when it carries nothing. Each group is its own staged form
 * (reusing the Security tab's control), writing back to `/config/:section`.
 *
 * It fetches on mount, and the accordion above it mounts its children only when
 * opened, so a closed section costs nothing.
 */
export function ConfigSectionCard({
  deviceId,
  section,
  onNotice,
}: {
  deviceId: string;
  section: string;
  onNotice: Notify;
}) {
  const queryKey = ['device', deviceId, 'config', section];
  const q = useQuery({
    queryKey,
    queryFn: () => api.get<ConfigSectionState>(`/devices/${enc(deviceId)}/config/${section}`),
  });

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }
  const groups = q.data?.groups ?? [];
  if (groups.length === 0) {
    return (
      <p className="p-5 text-sm text-muted">
        This ONU’s model exposes none of these settings over TR-069.
      </p>
    );
  }
  return (
    <div className="divide-y divide-line">
      {groups.map((group) => (
        <div key={group.key}>
          {groups.length > 1 && (
            <div className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-faint">
              {group.label}
            </div>
          )}
          <SecurityGroupControls
            group={group}
            deviceId={deviceId}
            onNotice={onNotice}
            postPath={`/devices/${enc(deviceId)}/config/${section}`}
            invalidateKeys={[queryKey, ['device', deviceId]]}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * On-demand diagnostics — the ONU runs a ping or a traceroute against a host and
 * reports back over TR-069. Starting one arms `DiagnosticsState=Requested`; this
 * then polls the read until the state settles, so the operator watches the run
 * from here instead of the CPE web UI. Both are standard TR-098 objects, so the
 * same UI serves every model — and a model without one shows an honest note.
 */
export function DiagnosticsCard({
  deviceId,
  onNotice,
}: {
  deviceId: string;
  onNotice: Notify;
}) {
  const { can } = useSession();
  const canWrite = can('device.write');
  const queryClient = useQueryClient();
  const queryKey = ['device', deviceId, 'diagnostics'];
  const [running, setRunning] = useState<null | 'ping' | 'traceroute'>(null);
  const [pingHost, setPingHost] = useState('8.8.8.8');
  const [pingCount, setPingCount] = useState('4');
  const [traceHost, setTraceHost] = useState('8.8.8.8');
  // A run in flight is work in progress: the Diagnostics panel keeps this
  // mounted (and polling) while the operator looks at another panel, so the
  // result is on screen when they come back rather than a stale first read.
  useUnsavedDraft(running !== null);

  const q = useQuery({
    queryKey,
    // While a run is in flight, ask the server to refresh that object from the
    // CPE before reading — GenieACS holds the armed "Requested" value until the
    // object is pulled fresh, so an un-refreshed poll would never see completion.
    queryFn: () =>
      api.get<DiagnosticsState>(
        `/devices/${enc(deviceId)}/diagnostics${running ? `?refresh=${running}` : ''}`,
      ),
    refetchInterval: running ? 3000 : false,
  });

  // Stop polling once the running diagnostic leaves the Requested state.
  useEffect(() => {
    if (!running || !q.data) return;
    const state = running === 'ping' ? q.data.ping?.state : q.data.traceRoute?.state;
    if (state && state !== 'Requested') setRunning(null);
  }, [running, q.data]);

  const start = useMutation({
    mutationFn: (body: RunDiagnosticRequest) =>
      api.post(`/devices/${enc(deviceId)}/diagnostics`, body),
    onSuccess: async (_r, body) => {
      setRunning(body.type);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not start', 'danger'),
  });

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const ping = q.data?.ping;
  const trace = q.data?.traceRoute;
  const pingSupported = q.data?.pingSupported;
  const traceSupported = q.data?.traceRouteSupported;
  const pingRunning = running === 'ping';
  const traceRunning = running === 'traceroute';

  return (
    <div className="divide-y divide-line">
      {/* Ping */}
      <div className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Ping</span>
          {ping?.state && <DiagState state={ping.state} />}
        </div>
        {pingSupported ? (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-muted">
                Host
                <Input
                  className="mt-1 w-52"
                  value={pingHost}
                  onChange={(e) => setPingHost(e.target.value)}
                  placeholder="8.8.8.8"
                />
              </label>
              <label className="text-xs text-muted">
                Count
                <Input
                  className="mt-1 w-20"
                  value={pingCount}
                  onChange={(e) => setPingCount(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </label>
              {canWrite && (
                <Button
                  variant="primary"
                  disabled={pingRunning || !pingHost.trim()}
                  onClick={() =>
                    start.mutate({
                      type: 'ping',
                      host: pingHost.trim(),
                      count: Math.min(64, Math.max(1, Number(pingCount) || 4)),
                    })
                  }
                >
                  {pingRunning ? 'Running…' : 'Run ping'}
                </Button>
              )}
            </div>
            {ping && ping.state !== 'Requested' && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                <Stat label="Host" value={ping.host ?? '—'} />
                <Stat label="Success" value={fmtCount(ping.successCount)} />
                <Stat label="Failed" value={fmtCount(ping.failureCount)} />
                <Stat label="Avg" value={fmtMs(ping.averageResponseMs)} />
                <Stat label="Min" value={fmtMs(ping.minResponseMs)} />
                <Stat label="Max" value={fmtMs(ping.maxResponseMs)} />
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">This ONU does not expose ping over TR-069.</p>
        )}
      </div>

      {/* Traceroute */}
      <div className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Traceroute</span>
          {trace?.state && <DiagState state={trace.state} />}
        </div>
        {traceSupported ? (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-muted">
                Host
                <Input
                  className="mt-1 w-52"
                  value={traceHost}
                  onChange={(e) => setTraceHost(e.target.value)}
                  placeholder="8.8.8.8"
                />
              </label>
              {canWrite && (
                <Button
                  variant="primary"
                  disabled={traceRunning || !traceHost.trim()}
                  onClick={() => start.mutate({ type: 'traceroute', host: traceHost.trim() })}
                >
                  {traceRunning ? 'Running…' : 'Run traceroute'}
                </Button>
              )}
            </div>
            {trace && trace.state !== 'Requested' && trace.hops.length > 0 && (
              <ol className="space-y-1 text-sm">
                {trace.hops.map((h, i) => (
                  <li key={i} className="flex gap-3 tabular-nums">
                    <span className="w-6 text-right text-faint">{i + 1}</span>
                    <span className="text-ink">{h.hostAddress || h.host || '*'}</span>
                    {h.host && h.hostAddress && h.host !== h.hostAddress && (
                      <span className="text-muted">{h.host}</span>
                    )}
                  </li>
                ))}
              </ol>
            )}
            {trace && trace.state !== 'Requested' && trace.hops.length === 0 && (
              <p className="text-sm text-muted">No hops reported.</p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">This ONU does not expose traceroute over TR-069.</p>
        )}
      </div>
    </div>
  );
}

/**
 * VoIP (SIP) line status — read-only. Answers the one question an operator asks
 * about a subscriber's phone: did the line register? Empty on the data-only ONUs
 * that are most of the fleet, which is why it lives behind its own accordion.
 */
export function VoipCard({ deviceId }: { deviceId: string }) {
  const q = useQuery({
    queryKey: ['device', deviceId, 'voip'],
    queryFn: () => api.get<VoipState>(`/devices/${enc(deviceId)}/voip`),
  });

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }
  const profiles = q.data?.profiles ?? [];
  if (!q.data?.supported || profiles.length === 0) {
    return (
      <p className="p-5 text-sm text-muted">
        This ONU has no voice service — it is a data-only unit.
      </p>
    );
  }

  return (
    <div className="divide-y divide-line">
      {profiles.map((profile) => (
        <div key={profile.index} className="space-y-3 p-5">
          {(profile.proxyServer || profile.registrarServer) && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              <Stat
                label="SIP proxy"
                value={
                  profile.proxyServer
                    ? `${profile.proxyServer}${profile.proxyServerPort ? `:${profile.proxyServerPort}` : ''}`
                    : '—'
                }
              />
              <Stat label="Registrar" value={profile.registrarServer ?? '—'} />
              <Stat label="Domain" value={profile.userAgentDomain ?? '—'} />
            </div>
          )}
          <div className="space-y-2">
            {profile.lines.map((line) => (
              <div
                key={line.index}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="text-sm text-ink">
                    Line {line.index}
                    {line.directoryNumber ? ` · ${line.directoryNumber}` : ''}
                  </span>
                  {line.uri && <p className="truncate text-xs text-faint">{line.uri}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {line.enabled === false && <Pill tone="neutral">Disabled</Pill>}
                  {line.status && <RegState status={line.status} />}
                  {line.callState && line.callState !== 'Idle' && (
                    <Pill tone="info">{line.callState}</Pill>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- small presentational helpers --------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-faint">{label}</div>
      <div className="truncate text-ink tabular-nums">{value}</div>
    </div>
  );
}

function DiagState({ state }: { state: string }) {
  if (state === 'Requested') return <Pill tone="info">Running…</Pill>;
  if (state === 'Complete') return <Pill tone="success">Complete</Pill>;
  if (state === 'None') return <Pill tone="neutral">Not run</Pill>;
  return <Pill tone="danger">{state.replace(/^Error_?/, 'Error: ')}</Pill>;
}

function RegState({ status }: { status: string }) {
  const tone = /^(up|registered)$/i.test(status)
    ? 'success'
    : /register/i.test(status)
      ? 'info'
      : 'danger';
  return <Pill state tone={tone as 'success' | 'info' | 'danger'}>{status}</Pill>;
}

function fmtMs(v: number | null): string {
  return v === null || v === undefined ? '—' : `${v} ms`;
}
function fmtCount(v: number | null): string {
  return v === null || v === undefined ? '—' : String(v);
}
